import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { api, getStoredToken, PreferenceResponse, setStoredToken } from "./api/client";
import { isIos, isStandalone } from "./lib/platform";
import { enablePushNotifications, ensureServiceWorker } from "./lib/push";

type Screen = "loading" | "welcome" | "verify" | "home";

export function App() {
  const [screen, setScreen] = useState<Screen>("loading");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [userUuid, setUserUuid] = useState<string | null>(null);
  const [preferences, setPreferences] = useState<PreferenceResponse[]>([]);
  const [pushReady, setPushReady] = useState(false);

  const needsIosInstall = useMemo(() => isIos() && !isStandalone(), []);

  const refreshPreferences = useCallback(async () => {
    const list = await api.listPreferences();
    setPreferences(list.filter((p) => p.channel === "web_push"));
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        await ensureServiceWorker();
      } catch {
        /* ignore on unsupported browsers during first paint */
      }

      const token = getStoredToken();
      if (!token) {
        setScreen("welcome");
        return;
      }

      try {
        const me = await api.me();
        setUserUuid(me.userUuid);
        await refreshPreferences();
        const subs = await api.listSubscriptions();
        setPushReady(subs.length > 0);
        setScreen("home");
      } catch {
        setStoredToken(null);
        setScreen("welcome");
      }
    })();
  }, [refreshPreferences]);

  async function onQuickStart() {
    setBusy(true);
    setError(null);
    try {
      const result = await api.register(email || undefined, displayName || undefined);
      setStoredToken(result.accessToken);
      setUserUuid(result.userUuid);
      await refreshPreferences();
      setScreen("home");
      setMessage("Account created. Enable notifications to receive push on this device.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create account.");
    } finally {
      setBusy(false);
    }
  }

  async function onVerify(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api.authVerify(email, code, displayName || undefined);
      setStoredToken(result.accessToken);
      setUserUuid(result.userUuid);
      await refreshPreferences();
      setScreen("home");
      setMessage("Signed in.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid code.");
    } finally {
      setBusy(false);
    }
  }

  async function onEnablePush() {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      if (needsIosInstall) {
        throw new Error("On iPhone/iPad, add this app to the Home Screen and open it from the icon first.");
      }
      await enablePushNotifications();
      setPushReady(true);
      setMessage("Notifications enabled for this device.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not enable notifications.");
    } finally {
      setBusy(false);
    }
  }

  async function onTogglePreference(pref: PreferenceResponse, optedIn: boolean) {
    setError(null);
    try {
      await api.setPreference(pref.sourceKey, pref.typeKey, pref.channel, optedIn);
      setPreferences((current) =>
        current.map((item) =>
          item.sourceKey === pref.sourceKey &&
          item.typeKey === pref.typeKey &&
          item.channel === pref.channel
            ? { ...item, optedIn }
            : item
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update preference.");
    }
  }

  async function onTestSend() {
    setBusy(true);
    setError(null);
    try {
      const result = await api.testSend(
        "CNP test",
        "If you see this, web push delivery works.",
        "/"
      );
      setMessage(`Test queued (${result.enqueued} item(s)). Wait a few seconds.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Test send failed.");
    } finally {
      setBusy(false);
    }
  }

  function signOut() {
    setStoredToken(null);
    setUserUuid(null);
    setPreferences([]);
    setPushReady(false);
    setScreen("welcome");
  }

  return (
    <div className="app">
      <header>
        <h1>CNP Preferences</h1>
        <p>Centralized Notification Platform — enable push and manage opt-ins.</p>
      </header>

      {message && <div className="banner info">{message}</div>}
      {error && <div className="banner error">{error}</div>}

      {needsIosInstall && (
        <div className="banner warn">
          <strong>iPhone / iPad:</strong> Web push only works from a Home Screen app.
          <ol className="steps">
            <li>Tap Share in Safari</li>
            <li>Choose <em>Add to Home Screen</em></li>
            <li>Open CNP from the new icon, then enable notifications</li>
          </ol>
        </div>
      )}

      {screen === "loading" && <div className="card muted">Loading…</div>}

      {screen === "welcome" && (
        <div className="card stack">
          <h2>Get started</h2>
          <p className="muted">
            Create a Hub identity on this device, or sign in with email to reclaim an existing one.
          </p>
          <label>
            Display name (optional)
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Ada" />
          </label>
          <label>
            Email (optional for quick start; required to reclaim later)
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </label>
          <div className="row">
            <button type="button" disabled={busy} onClick={() => void onQuickStart()}>
              Continue on this device
            </button>
            <button
              type="button"
              className="secondary"
              disabled={busy || !email}
              onClick={() => {
                void (async () => {
                  setBusy(true);
                  setError(null);
                  try {
                    await api.authStart(email);
                    setScreen("verify");
                    setMessage(
                      "Check your email for a 6-digit code. Enter it here (important on iOS Home Screen apps)."
                    );
                  } catch (err) {
                    setError(err instanceof Error ? err.message : "Could not send code.");
                  } finally {
                    setBusy(false);
                  }
                })();
              }}
            >
              Email me a code
            </button>
          </div>
        </div>
      )}

      {screen === "verify" && (
        <form className="card stack" onSubmit={(e) => void onVerify(e)}>
          <h2>Enter sign-in code</h2>
          <p className="muted">Sent to {email}</p>
          <label>
            6-digit code
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
            />
          </label>
          <div className="row">
            <button type="submit" disabled={busy || code.trim().length < 4}>
              Verify
            </button>
            <button type="button" className="secondary" onClick={() => setScreen("welcome")}>
              Back
            </button>
          </div>
        </form>
      )}

      {screen === "home" && (
        <>
          <div className="card stack">
            <h2>Push notifications</h2>
            <p className="muted">
              Signed in as <code>{userUuid}</code>
            </p>
            <div className="row">
              <button
                type="button"
                className="success"
                disabled={busy || needsIosInstall}
                onClick={() => void onEnablePush()}
              >
                {pushReady ? "Re-register this device" : "Enable notifications"}
              </button>
              <button type="button" className="secondary" disabled={busy} onClick={() => void onTestSend()}>
                Send test push
              </button>
              <button type="button" className="secondary" onClick={signOut}>
                Sign out
              </button>
            </div>
            {pushReady && <p className="muted">At least one subscription is registered for your account.</p>}
          </div>

          <div className="card">
            <h2>Notification types</h2>
            {preferences.length === 0 && <p className="muted">No notification types yet.</p>}
            {preferences.map((pref) => (
              <div className="pref-item" key={`${pref.sourceKey}:${pref.typeKey}:${pref.channel}`}>
                <div>
                  <strong>{pref.typeDisplayName}</strong>
                  <span className="muted">
                    {pref.sourceName} · {pref.category}
                  </span>
                </div>
                <label className="switch" aria-label={`Toggle ${pref.typeDisplayName}`}>
                  <input
                    type="checkbox"
                    checked={pref.optedIn}
                    onChange={(e) => void onTogglePreference(pref, e.target.checked)}
                  />
                  <span className="slider" />
                </label>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
